import React, { useState } from 'react'
import { FormControl, FormLabel, Radio, RadioGroup } from '@chakra-ui/react'
import {
  type MappingDetails,
  type MappingListItem,
  type MappingLocation,
  type TenantCommodity,
  type UpdateCommodityMapping,
  useUpdateCQGSymbolTenantCommodityMapping,
} from 'src/api/queries/microservices/trade-commodity-config'
import { Box, Button, HStack, Row, Stack, Text } from 'src/ui'
import { useToast } from 'src/utils/toast'
import TenantCommodityPicker from './tenant-commodity-picker'

type MappingScope = 'tenant' | 'location'

type MappingEditorFormProps = {
  symbol: MappingListItem
  mapping: MappingDetails
  locations: MappingLocation[]
  tenantId: string
  canEdit: boolean
  onClose: () => void
}

export default function MappingEditorForm({
  symbol,
  mapping,
  locations,
  tenantId,
  canEdit,
  onClose,
}: MappingEditorFormProps) {
  const toast = useToast()
  const { mutateAsync: updateMapping, isPending } = useUpdateCQGSymbolTenantCommodityMapping()
  const globalCommodity =
    mapping.mapping.type === 'global' ? mapping.mapping.data.tenantCommodity : null

  const [scope, setScope] = useState<MappingScope>(
    mapping.mapping.type === 'perLocation' ? 'location' : 'tenant'
  )
  const [tenantCommodity, setTenantCommodity] = useState<TenantCommodity | null>(globalCommodity)
  const [locationCommodities, setLocationCommodities] = useState<
    Record<string, TenantCommodity | null>
  >(() =>
    locations.reduce<Record<string, TenantCommodity | null>>((result, location) => {
      result[location.bushelLocationId] = location.tenantCommodity
      return result
    }, {})
  )

  const handleSave = async () => {
    const selectedMappings = locations.flatMap((location) => {
      const commodity = locationCommodities[location.bushelLocationId]
      return commodity
        ? [
            {
              bushelLocationId: location.bushelLocationId,
              tenantCommodityId: commodity.id,
            },
          ]
        : []
    })

    const nextMapping: UpdateCommodityMapping =
      scope === 'tenant'
        ? tenantCommodity
          ? { type: 'global', data: { tenantCommodityId: tenantCommodity.id } }
          : { type: 'unmapped', data: {} }
        : selectedMappings.length
          ? { type: 'perLocation', data: { mappings: selectedMappings } }
          : { type: 'unmapped', data: {} }

    try {
      await updateMapping({
        categorySymbol: symbol.categorySymbol,
        mapping: nextMapping,
      })
      toast({ description: `Saved mapping for ${symbol.categorySymbol}` })
      onClose()
    } catch {
      toast({ status: 'error', description: 'Failed to save commodity mapping.' })
    }
  }

  return (
    <Box bg="white" borderRadius={3} boxShadow="md" p={6}>
      <Row justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Text fontSize="lg" fontWeight="600">
            {symbol.categorySymbol}
          </Text>
          <Text color="gray.600">{symbol.categorySymbolName}</Text>
        </Box>
        <HStack>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            colorScheme="primary"
            isLoading={isPending}
            isDisabled={!canEdit}
            onClick={handleSave}
          >
            Save
          </Button>
        </HStack>
      </Row>

      <FormControl as="fieldset" mb={5}>
        <FormLabel as="legend" fontWeight="600">
          Mapping scope
        </FormLabel>
        <RadioGroup value={scope} onChange={(value) => setScope(value as MappingScope)}>
          <HStack spacing={6}>
            <Radio value="tenant" isDisabled={!canEdit}>
              Tenant
            </Radio>
            <Radio value="location" isDisabled={!canEdit || !locations.length}>
              Per location
            </Radio>
          </HStack>
        </RadioGroup>
      </FormControl>

      {scope === 'tenant' ? (
        <FormControl>
          <FormLabel>Tenant commodity</FormLabel>
          <TenantCommodityPicker
            id={`tenant-commodity-${symbol.categorySymbol}`}
            tenantId={tenantId}
            value={tenantCommodity}
            placeholder="Select a tenant commodity, or leave empty to clear"
            isDisabled={!canEdit}
            onChange={setTenantCommodity}
          />
        </FormControl>
      ) : (
        <Stack spacing={3}>
          <Text fontWeight="600">Tenant commodity by location</Text>
          {locations.map((location) => (
            <HStack key={location.bushelLocationId} alignItems="center">
              <Text width="180px" flexShrink={0}>
                {location.locationName}
              </Text>
              <Box flex="1" minWidth={0}>
                <TenantCommodityPicker
                  id={`tenant-commodity-${symbol.categorySymbol}-${location.bushelLocationId}`}
                  tenantId={tenantId}
                  value={locationCommodities[location.bushelLocationId] ?? null}
                  placeholder="Unmapped"
                  isDisabled={!canEdit}
                  onChange={(commodity) =>
                    setLocationCommodities((current) => ({
                      ...current,
                      [location.bushelLocationId]: commodity,
                    }))
                  }
                />
              </Box>
            </HStack>
          ))}
        </Stack>
      )}
    </Box>
  )
}
